import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { ConsumptionMeter } from '/imports/api/consumptionMeters';

export interface UtilityAccount extends Doc {
  orgId: string;
  name?: string;
  username?: string;
  password?: string;
  email?: string;
  type?: 'sdge' | 'pge' | 'sce';
  status: 'new' | 'authorized' | 'expired' | 'reauth';
  projectId?: string;
  projectManagementProjectId?: string;  // PM-module anchor (send-a-link consumption capture)
  pmCapturedAt?: Date;                  // set once the captured intervals were exported into the PM doc slot
  expireAt?: string;
  setupAutomation?: boolean;
  mfaCode?: string;
  browserURL?: string;
  httpURL?: string;
  pullIntervals?: { accountNumber: string, identifier: string, startTime: Date, endTime: Date }[];
  _meters?: ConsumptionMeter[];
}
export type Model = UtilityAccount;

export const UtilityAccounts = new Mongo.Collection<UtilityAccount>('utilityAccounts');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: UtilityAccounts,
  editableFields: ['active', 'orgId', 'name', 'username', 'password', 'email', 'type', 'expireAt',
  ],
  fieldLimits: {
    'email': 320, 'expireAt': 64, 'name': 256, 'orgId': 64, 'password': 4096, 'type': 64, 'username': 256,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<UtilityAccount>, ListMethodResponse<UtilityAccount, Refs>>('listUtilityAccounts'),
  getMethod: DeclareMethod<string, GetMethodResponse<UtilityAccount, Refs>>('getUtilityAccount'),
  createMethod: DeclareMethod<Partial<UtilityAccount>, string>('createUtilityAccount'),
  updateMethod: DeclareMethod<Partial<UtilityAccount>, string>('updateUtilityAccount'),
  deleteMethod: DeclareMethod<string>('deleteUtilityAccount'),
  pathPrefix: '/utility-accounts',
  asString: (document) => document.name || document.username || document._id,
  modelName: strings.UtilityAccount(),
  pluralName: strings.UtilityAccounts(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { username: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
        { billingAccountNumber: new RegExp(search, 'gi') },
        { type: search as UtilityAccount['type'] },
      ]
    };
  },
  validate: async (project: Partial<UtilityAccount>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'orgId', { required: true });
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'status', { required: true });
    return errors;
  },
};

export const makeUtilityAccountLinkForProjectId = DeclareMethod<string, string>('makeUtilityAccountLinkForProjectId');
export const makeNewUtilityAccountLink = DeclareMethod<{ orgId?: string, hid?: string }, string>('makeNewUtilityAccountLink');
interface SdgeUtilityAccountConnectionArg { _id: string, email: string, username: string, password: string, type: UtilityAccount['type'], expireAt?: string, rtid: string, hid?: string }
export const sdgeUtilityAccountConnection = DeclareMethod<SdgeUtilityAccountConnectionArg, void>('sdgeUtilityAccountConnection');
interface PgeUtilityAccountConnectionArg { _id: string, email: string, username: string, password: string, type: UtilityAccount['type'], expireAt?: string, rtid: string, hid?: string }
export const pgeUtilityAccountConnection = DeclareMethod<PgeUtilityAccountConnectionArg, void>('pgeUtilityAccountConnection');
interface SceUtilityAccountConnectionArg { _id: string, email: string, username: string, password: string, type: UtilityAccount['type'], expireAt?: string, rtid: string, hid?: string }
export const sceUtilityAccountConnection = DeclareMethod<SceUtilityAccountConnectionArg, void>('sceUtilityAccountConnection');
export const processSdgeIntervals = DeclareMethod<void, void>('processSdgeIntervals');
export const processSdgeBills = DeclareMethod<void, void>('processSdgeBills');
export const decryptUtilityAccountPassword = DeclareMethod<string, string>('decryptUtilityAccountPassword');
export const updateUtilityAccountMfaCode = DeclareMethod<{ _id: string, mfaCode: string }, void>('updateUtilityAccountMfaCode');
export const clearAutomationProfile = DeclareMethod<string, void>('clearAutomationProfile');

Meteor.isServer && Meteor.startup(async () => {
  await UtilityAccounts.createIndexAsync({ orgId: 1 });
  await UtilityAccounts.createIndexAsync({ name: 1 });
  await UtilityAccounts.createIndexAsync({ type: 1 });
  await UtilityAccounts.createIndexAsync({ username: 1, type: 1 }, { unique: true, sparse: true });
});

/**
 * Maps a utility account type code to its display label.
 * @param utilityAccount - account whose type to format
 * @returns display label (e.g. "SDG&E"), or the raw type if unrecognized
 */
export const utilityAccountType = (utilityAccount?: { type?: string }) => {
  if (!utilityAccount) return '';
  if (utilityAccount.type === 'sdge') return 'SDG&E';
  if (utilityAccount.type === 'pge') return 'PG&E';
  if (utilityAccount.type === 'sce') return 'SCE';
  return utilityAccount.type;
};

export const utilityOptions = [
  { value: '', label: '' },
  { value: 'sdge', label: 'SDG&E' },
  { value: 'pge', label: 'PG&E' },
  { value: 'sce', label: 'SCE' },
];